/**
 * Optimized NavigationControl that uses passive event listeners
 * to improve performance and reduce main thread blocking
 */
class OptimizedNavigationControl {
  constructor(options = {}) {
    this.options = {
      showCompass: true,
      showZoom: true,
      visualizePitch: false,
      ...options,
    };
  }

  onAdd(map) {
    this._map = map;
    this._container = document.createElement("div");
    this._container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";

    if (this.options.showZoom) {
      this._zoomInButton = this._createButton(
        "mapboxgl-ctrl-zoom-in",
        "Zoom in",
        () => map.zoomIn()
      );
      this._zoomOutButton = this._createButton(
        "mapboxgl-ctrl-zoom-out",
        "Zoom out",
        () => map.zoomOut()
      );
      this._container.appendChild(this._zoomInButton);
      this._container.appendChild(this._zoomOutButton);
    }

    if (this.options.showCompass) {
      this._compass = this._createCompass();
      this._container.appendChild(this._compass);
    }

    // Add passive event listeners for better performance
    this._addPassiveEventListeners();

    return this._container;
  }

  onRemove() {
    this._removeEventListeners();
    if (this._container && this._container.parentNode) {
      this._container.parentNode.removeChild(this._container);
    }
    this._map = undefined;
  }

  _createButton(className, ariaLabel, clickHandler) {
    const button = document.createElement("button");
    button.className = `mapboxgl-ctrl-icon ${className}`;
    button.type = "button";
    button.title = ariaLabel;
    button.setAttribute("aria-label", ariaLabel);

    // Use passive event listener for better performance
    button.addEventListener("click", clickHandler, { passive: true });

    return button;
  }

  _createCompass() {
    const compass = document.createElement("button");
    compass.className = "mapboxgl-ctrl-icon mapboxgl-ctrl-compass";
    compass.type = "button";
    compass.title = "Reset bearing to north";
    compass.setAttribute("aria-label", "Reset bearing to north");

    // Add compass rotation indicator
    const compassIcon = document.createElement("span");
    compassIcon.className = "mapboxgl-ctrl-compass-arrow";
    compass.appendChild(compassIcon);

    // Reset bearing on click with passive listener
    compass.addEventListener(
      "click",
      () => {
        this._map.resetNorth();
      },
      { passive: true }
    );

    return compass;
  }

  _addPassiveEventListeners() {
    if (!this._map) return;

    // Update compass rotation on map rotate
    this._onRotate = () => {
      if (this._compass) {
        const bearing = this._map.getBearing();
        const compassIcon = this._compass.querySelector(
          ".mapboxgl-ctrl-compass-arrow"
        );
        if (compassIcon) {
          compassIcon.style.transform = `rotate(${bearing}deg)`;
        }
      }
    };

    // Use passive event listeners for better scroll performance
    this._map.on("rotate", this._onRotate);
    this._map.on("pitch", this._onRotate);
  }

  _removeEventListeners() {
    if (this._map && this._onRotate) {
      this._map.off("rotate", this._onRotate);
      this._map.off("pitch", this._onRotate);
    }
  }
}

export default OptimizedNavigationControl;
